import { TFunction } from 'i18next';
import slugify from '@sindresorhus/slugify';
import { StorefrontCollectionLayout } from '../enums/storefront-collection-layout.enum';
import { StorefrontError } from '../enums/storefront-error.enum';
import { IStorefrontCollectionFormValue } from '../interfaces/storefront-form.interface';
import { IStorefrontCollection } from '../interfaces/storefront-collection.interface';
import { IStorefrontCollectionInput } from '../services/StorefrontService';
import { SHOP_URL } from '../constants';

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

export const UUID_REGEX = new RegExp(`^${UUID}$`, 'i');

// Anchored, so only a link whose whole path is the shop's product path is read:
// /product/<id> or /product/<id>/<name>, with a trailing slash, a query or a
// fragment allowed after it. Anything else, /product/<id> buried in some other
// path for instance, is reported back as unreadable. The host is deliberately
// not checked, the shop answers on several of them.
const PRODUCT_PATH_REGEX = new RegExp(
  `^/product/(${UUID})(?:/([^/?#]+))?/?(?:[?#].*)?$`,
  'i',
);

// Everything up to the first slash of the path: an optional scheme and host.
const ORIGIN_REGEX = /^(?:[a-z][a-z0-9+.-]*:)?(?:\/\/)?[^/]*/i;

export interface IProductLink {
  productId: string;
  // The name segment a shop link carries after the id, absent when a partner
  // pasted a bare id or a link without one.
  slug?: string;
}

// Matching the raw string instead of using new URL(), because partners paste
// links without a scheme and new URL() would throw on those.
export const parseProductLink = (value: string): IProductLink | undefined => {
  const input = value.trim();

  if (UUID_REGEX.test(input)) {
    return { productId: input.toLowerCase() };
  }

  const match = input.replace(ORIGIN_REGEX, '').match(PRODUCT_PATH_REGEX);

  return match
    ? { productId: match[1].toLowerCase(), slug: match[2] }
    : undefined;
};

// Slugifying a name throws away its punctuation, its casing and its accents, so
// this reads back something close to the name rather than the name itself. It is
// only ever shown as a placeholder until the api answers with the real one.
export const unslugify = (slug: string): string => {
  return slug
    .split('-')
    .filter((word) => word !== '')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// UIkit renders a tooltip's title as html rather than as text, so anything
// that could ever carry user input must go through this first.
export const escapeHtml = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
};

export interface IProductToken extends IProductLink {
  // The exact token as pasted, so the original link can be kept around and a
  // product can be opened in the shop again.
  raw: string;
}

// Splitting on whitespace and commas lets a partner paste a whole batch of
// product links at once. This is the single place a pasted value is read;
// every caller picks what it needs from the tokens.
export const parseProductTokens = (
  value: string,
): { tokens: IProductToken[]; invalid: string[] } => {
  const parts = value.split(/[\s,]+/).filter((part) => part !== '');

  const tokens: IProductToken[] = [];
  const invalid: string[] = [];

  parts.forEach((part) => {
    const link = parseProductLink(part);

    if (link) {
      tokens.push({ ...link, raw: part });
    } else {
      invalid.push(part);
    }
  });

  return { tokens, invalid };
};

export const parseProductIds = (
  value: string,
): { productIds: string[]; invalid: string[] } => {
  const { tokens, invalid } = parseProductTokens(value);

  const productIds: string[] = [];

  tokens.forEach((token) => {
    if (!productIds.includes(token.productId)) {
      productIds.push(token.productId);
    }
  });

  return { productIds, invalid };
};

export const shortenProductId = (productId: string): string => {
  return `${productId.slice(0, 8)}…${productId.slice(-4)}`;
};

// The shop looks a product up by its id, the slug that follows is cosmetic and
// only appended when the api resolved a name for the product.
export const getProductUrl = (productId: string, name?: string): string => {
  return name
    ? `${SHOP_URL}/product/${productId}/${slugify(name)}`
    : `${SHOP_URL}/product/${productId}`;
};

export const createEmptyCollection = (): IStorefrontCollectionFormValue => {
  return {
    key:
      typeof crypto?.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    name: '',
    description: '',
    layout: StorefrontCollectionLayout.ROW_M,
    productIds: [],
    productUrls: {},
    provisionalNames: {},
  };
};

export const toCollectionFormValues = (
  collections: IStorefrontCollection[],
): IStorefrontCollectionFormValue[] => {
  return [...collections]
    .sort((a, b) => a.order - b.order)
    .map((collection) => ({
      key: collection.id,
      id: collection.id,
      name: collection.name,
      description: collection.description ?? '',
      layout: collection.layout,
      productIds: collection.products.map((product) => product.productId),
      productUrls: {},
      provisionalNames: {},
    }));
};

// The form only carries ids, the names the api resolved for them are kept aside
// so a product can be listed by name instead of by id.
export const toProductNames = (
  collections: IStorefrontCollection[],
): Record<string, string> => {
  const names: Record<string, string> = {};

  collections.forEach((collection) => {
    collection.products.forEach((product) => {
      names[product.productId] = product.name;
    });
  });

  return names;
};

// The order is derived from the position in the list, which keeps it dense and
// unique and makes DUPLICATE_COLLECTION_ORDER unreachable. The client-only key
// and the pasted link maps are dropped here so they can never end up in a
// request body.
export const toCollectionInputs = (
  collections: IStorefrontCollectionFormValue[],
): IStorefrontCollectionInput[] => {
  return collections.map((collection, index) => ({
    id: collection.id,
    name: collection.name,
    description: collection.description,
    layout: collection.layout,
    order: index,
    productIds: collection.productIds,
  }));
};

const STOREFRONT_ERROR_MESSAGE_KEYS: Record<StorefrontError, string> = {
  [StorefrontError.STOREFRONT_ALREADY_EXISTS]: 'Errors.storefrontAlreadyExists',
  [StorefrontError.STOREFRONT_NOT_FOUND]: 'Errors.storefrontNotFound',
  [StorefrontError.SLUG_ALREADY_TAKEN]: 'Errors.storefrontSlugTaken',
  [StorefrontError.COLLECTION_NOT_FOUND]: 'Errors.storefrontCollectionNotFound',
  [StorefrontError.DUPLICATE_COLLECTION_ORDER]:
    'Errors.storefrontDuplicateCollectionOrder',
  [StorefrontError.INVALID_PRODUCT_IDS]: 'Errors.storefrontInvalidProductIds',
};

export const getStorefrontErrorCode = (e: any): StorefrontError | undefined => {
  return e?.response?.data?.errorCode as StorefrontError | undefined;
};

export const getStorefrontErrorMessage = (e: any, t: TFunction): string => {
  const errorCode = getStorefrontErrorCode(e);
  const key = errorCode ? STOREFRONT_ERROR_MESSAGE_KEYS[errorCode] : undefined;

  if (key) {
    // @ts-ignore the typed translation keys do not narrow through a lookup table
    return t(key);
  }

  // A 400 without an error code is the api rejecting the body itself. It strips
  // the field-level detail off those, so this is as specific as we can be.
  if (e?.response?.status === 400) {
    return t('Errors.storefrontInvalidInput');
  }

  return t('Errors.general');
};
